import { useDispatch, useSelector } from "react-redux";
import {
  closeConversation,
  typingConversation,
  getContactSuggestionPerPage,
  getConversationsSearch,
  updateContact,
  getConversations,
  sendMessages,
  postOnline,
  getConversation,
  updateConversation,
  takeConversation,
  transferConversation,
  downloadMessageMedia,
} from "apis/conversation";
import { sortById } from "utils/helper";
import {
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
  // useSuspenseQuery,
} from "@tanstack/react-query";
import {
  setChat,
  setConversation,
  setConversationType,
  setIsFetchingConversation,
  mergeMessages,
  pinConversation,
  unpinConversation,
} from "appredux/chatSlice";
import { store } from "appredux/store";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  conversationTypeSelector,
  isAssignModeSelector,
} from "appredux/selectors";
import { useAntdMessage } from "hooks/useAntdMessage";
import { cloneDeep, includes, isEmpty, omit } from "lodash-es";
import { conversationStatuses } from "utils/static";

// Timeout pendek untuk fetch detail conversation saat dibuka (jalur realtime).
const OPEN_DETAIL_TIMEOUT_MS = 20000;

// Dedupe getConversation yang sedang berjalan (mencegah double-fetch saat
// conversation dibuka beruntun cepat). BUKAN penanda "sudah loaded" — tidak
// menyimpan state, di-clear di finally, jadi tidak bisa stale.
const inFlightDetailIds = new Set();

export function useConversations(params, options = {}) {
  const dispatch = useDispatch();
  const isAssignMode = useSelector(isAssignModeSelector);
  const queryKey = !isAssignMode
    ? ["conversations", "list"]
    : ["conversations", "list", params];
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      let response;
      // Waktu request dikirim. Dipakai countdown auto-transfer untuk tahu apakah
      // list diambil dari server SETELAH batas waktu (bukan cache basi). Field
      // ini ikut terbawa saat cache di-update Pusher via `{ ...old, data }`.
      const fetchedAt = Date.now();
      try {
        const {
          data: { data },
        } = await getConversations(params);
        response = data ? { ...data, fetched_at: fetchedAt } : data;
      } catch (error) {
        dispatch(setIsFetchingConversation(false));
        throw new Error("Gagal mengambil data dari server!");
      }
      dispatch(setIsFetchingConversation(false));
      return response;
    },
    placeholderData: { data: [] },
    ...options,
  });

  useEffect(() => {
    if (query.isFetching) {
      dispatch(setIsFetchingConversation(true));
    }
  }, [dispatch, query.isFetching]);

  return query;
}

// Buka conversation ke panel chat: tampil instan dari cache, lalu (sekali per
// conversation per sesi) muat history penuh via getConversation dan merge ke
// redux. Mengatasi kasus conversation baru yang messages-nya minim di cache
// (panel hanya menampilkan pesan Pusher, history kosong sampai refresh).
export const useOpenConversation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const isAssignMode = useSelector(isAssignModeSelector);
  const conversationType = useSelector(conversationTypeSelector);
  const queryKey = useMemo(() => {
    return !isAssignMode
      ? ["conversations", "list"]
      : ["conversations", "list", { inbox_filter: conversationType }];
  }, [conversationType, isAssignMode]);

  return useCallback(
    async (item) => {
      if (isEmpty(item)) return;
      const cache = queryClient.getQueryData(queryKey);
      const cacheConv = cache?.data?.find((row) => row.id === item.id);

      // 1) Instan dari cache
      dispatch(
        setChat({
          conversation: omit(item, ["messages", "counter"]),
          messages: cacheConv?.messages || [],
        }),
      );

      // 2) Fetch history penuh HANYA bila cache belum lengkap:
      // - !cacheConv → dibuka dari search/navigasi, tak ada di list
      // - __historyLoaded === false → embedded broadcast (messages minim)
      // - isEmpty(messages) → safety-net untuk minim dari path tak tertandai
      // Kalau sudah lengkap (dari list / sudah pernah di-fetch) → tampil instan,
      // tanpa network.
      const needDetail =
        !cacheConv ||
        cacheConv.__historyLoaded === false ||
        isEmpty(cacheConv.messages);
      // inFlightDetailIds: cegah double-fetch saat open beruntun cepat (flag
      // __historyLoaded baru di-set setelah fetch resolve).
      if (!needDetail || inFlightDetailIds.has(item.id)) return;
      inFlightDetailIds.add(item.id);

      // Tahan-gagal; gagal → panel tetap dari cache.
      try {
        const { data } = await getConversation(item.id, undefined, {
          timeout: OPEN_DETAIL_TIMEOUT_MS,
        });
        const full = data?.data;
        if (isEmpty(full?.messages)) return;
        // Guard anti-race: hanya apply kalau conversation yang terbuka masih sama
        if (store.getState().chat.conversation?.id === item.id) {
          const reduxIds = new Set(
            (store.getState().chat.messages || []).map((m) => m.id),
          );
          if (full.messages.some((m) => !reduxIds.has(m.id))) {
            dispatch(mergeMessages(full.messages));
          }
        }
        // Update cache + tandai lengkap supaya buka berikutnya instan tanpa fetch
        // & broadcast nambah ke history yang sudah utuh.
        queryClient.setQueryData(queryKey, (old) => {
          if (!old?.data || !Array.isArray(old.data)) return old;
          return {
            ...old,
            data: old.data.map((row) =>
              row.id === item.id
                ? { ...row, messages: full.messages, __historyLoaded: true }
                : row,
            ),
          };
        });
      } catch (error) {
        console.error("useOpenConversation error:", error);
      } finally {
        inFlightDetailIds.delete(item.id);
      }
    },
    [queryClient, queryKey, dispatch],
  );
};

// Promosikan sebuah conversation ke tab my_inbox secara instan & tahan-glitch.
// Dipakai setelah agent "ambil alih" via kirim pesan dari search, atau membuat
// chat baru — keduanya otomatis menjadikan conversation milik agent yang login.
//
// Pendekatan: PIN saja (redux, DI LUAR query cache). Pin di-merge saat render
// list (lihat halaman conversations), jadi:
//   - tampil INSTAN & kebal refetch list yang masih lag di BE,
//   - TIDAK menyentuh query list → tidak memicu `isFetching` → tidak ada spinner
//     loading di tab yang sama (spinner sengaja dipertahankan untuk pindah tab).
// Pin auto-dibersihkan oleh efek unpin begitu conversation aslinya benar-benar
// masuk hasil fetch (mis. dari event Pusher) — sehingga tidak pernah ada celah
// "ada → hilang → ada".
//
// Urutan: pindah tab → pin dari `seed` (instan, bila ada) → getDetail otoritatif
// untuk melengkapi pin (history + current_agent_id terbaru). getDetail di sini
// adalah call terpisah, BUKAN query list, jadi tak memicu spinner.
export const usePromoteToMyInbox = () => {
  const dispatch = useDispatch();
  const isAssignMode = useSelector(isAssignModeSelector);
  const conversationType = useSelector(conversationTypeSelector);

  return useCallback(
    async (conversationId, { focus = true, seed } = {}) => {
      if (!conversationId) return;

      // 1) Pindah tab (kalau perlu) → saat landing di /list sudah my_inbox.
      if (isAssignMode && conversationType !== "my_inbox") {
        dispatch(setConversationType("my_inbox"));
      }

      // 2) Bila punya `seed` (data conv dari pemanggil), tampil & fokus SEKETIKA.
      if (!isEmpty(seed)) {
        // Normalisasi seed ke kondisi otoritatif yang SUDAH diketahui tanpa perlu
        // menunggu getDetail (langkah 3) — ini yang membuat baris langsung muncul
        // & langsung ter-highlight, bukan setelah satu roundtrip:
        // - id: BE membuat conversation BARU saat pesan dikirim ke conversation
        //   yang sudah ditutup, dan id barunya sudah dibawa response kirim pesan
        //   (`conversationId`). Tanpa ini baris di list (id baru) tidak pernah
        //   cocok dengan conversation aktif (id lama) → tidak pernah ter-highlight.
        // - status_id: agent baru saja mengirim pesan ke sini, jadi pasti OPEN.
        //   Seed dari hasil search bisa membawa status CLOSE yang basi sehingga
        //   barisnya tersaring dari list.
        const seedConv = {
          ...seed,
          id: conversationId,
          status_id: conversationStatuses.OPEN,
          messages: seed.messages || [],
        };
        dispatch(pinConversation(seedConv));
        // Fokus hanya bila seed membawa messages → jangan blank-kan panel.
        if (focus && !isEmpty(seedConv.messages)) {
          dispatch(
            setChat({
              conversation: omit(seedConv, "messages"),
              messages: seedConv.messages,
            }),
          );
        } else if (!focus) {
          // Sorot baris yang benar SEKETIKA tanpa menyentuh messages (lihat
          // cabang serupa di langkah 3).
          dispatch(setConversation(omit(seedConv, "messages")));
        }
      }

      // 3) Detail otoritatif (SEGERA, tak menunggu event). Endpoint detail tidak
      //    ikut lag seperti list my_inbox, dan ini bukan query list → tak memicu
      //    spinner.
      let detail;
      try {
        const { data } = await getConversation(conversationId, undefined, {
          timeout: OPEN_DETAIL_TIMEOUT_MS,
        });
        detail = data?.data;
      } catch (error) {
        console.error("usePromoteToMyInbox getConversation error:", error);
      }

      const conv = !isEmpty(detail)
        ? { ...detail, messages: detail.messages || [] }
        : !isEmpty(seed)
          ? { ...seed, messages: seed.messages || [] }
          : null;
      if (isEmpty(conv)) return;

      dispatch(pinConversation(conv)); // enrich / pin pertama (bila tak ada seed)
      if (focus && !isEmpty(conv.messages)) {
        dispatch(
          setChat({
            conversation: omit(conv, "messages"),
            messages: conv.messages,
          }),
        );
      } else if (!focus) {
        // focus:false (kirim dari search) → panel kanan sengaja TIDAK di-reset
        // supaya pesan optimistic yang barusan dikirim tidak hilang. Tapi
        // conversation aktif tetap harus disinkronkan ke id otoritatif, kalau
        // tidak baris di list tidak pernah ter-highlight (isSelected dibanding
        // dengan currentConversation.id) dan pengiriman berikutnya masih
        // memakai id conversation lama.
        dispatch(setConversation(omit(conv, "messages")));
        if (!isEmpty(conv.messages)) {
          // merge, bukan replace: pesan optimistic tetap tampil (union by id).
          dispatch(mergeMessages(conv.messages));
        }
      }
      // Tidak menulis ke query cache & tidak refetch di sini: itu yang dulu
      // memicu spinner di tab yang sama. Pin yang menahan tampilan sampai event
      // Pusher membawa conversation asli ke cache (lalu efek unpin membersihkan).
    },
    [dispatch, isAssignMode, conversationType],
  );
};

export const useSendMessage = (options = {}) => {
  return useMutation({
    mutationKey: ["sendMessage"],
    mutationFn: (payload) => sendMessages(payload),
    ...options,
  });
};

export const useSendMessageVariables = () => {
  const variables = useMutationState({
    filters: { mutationKey: ["sendMessage"], status: "pending" },
    select: (mutation) => mutation.state.variables,
  });

  return variables;
};

export const useCloseConversation = (options = {}) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const conversation_type = useSelector(conversationTypeSelector);
  const isAssignMode = useSelector(isAssignModeSelector);
  const queryKey = !isAssignMode
    ? ["conversations", "list"]
    : ["conversations", "list", { inbox_filter: conversation_type }];
  return useMutation({
    mutationKey: ["closeConversation"],
    mutationFn: ({ id, payload }) => closeConversation(id, payload),
    // When mutate is called:
    onMutate: async (payload) => {
      const previousData = queryClient.getQueryData(queryKey);
      // Optimistically update to the new value
      queryClient.setQueryData(queryKey, (old) => {
        const newData = { ...old };
        newData.data = newData.data.filter((row) => row.id !== payload.id);
        return newData;
      });

      // Pin hidup DI LUAR query cache (lihat usePromoteToMyInbox), jadi menghapus
      // dari query cache saja tidak cukup: pin akan tetap ter-merge di
      // formattedData dan conversation yang sudah ditutup "hidup lagi".
      // Efek unpin di halaman conversations hanya membersihkan pin yang muncul
      // di hasil fetch — conversation closed tidak akan pernah muncul lagi di
      // situ, jadi pin harus dibersihkan eksplisit di sini.
      const previousPin = store
        .getState()
        .chat.pinnedConversations.find((row) => row.id === payload.id);
      if (previousPin) {
        dispatch(unpinConversation(payload.id));
      }

      dispatch(setChat({ conversation: null, messages: [] }));

      // Return a context object with the snapshotted value
      return { previousData, previousPin };
    },
    onError: (err, newData, context) => {
      queryClient.setQueryData(queryKey, context.previousData);
      // Close gagal → kembalikan pin supaya conversation tidak ikut hilang.
      if (context?.previousPin) {
        dispatch(pinConversation(context.previousPin));
      }
    },
    ...options,
  });
};

export const useTypingConversation = (options = {}) => {
  return useMutation({
    mutationKey: ["typingConversation"],
    mutationFn: ({ id }) => typingConversation(id),
    ...options,
  });
};

export const useInfiniteContactSuggestion = (payload, options = {}) => {
  const { message } = useAntdMessage();
  return useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ["infinite/contacts/suggestion", payload],
    queryFn: async ({ pageParam }) => {
      let response;
      try {
        const {
          data: { data },
        } = await getContactSuggestionPerPage({
          page: pageParam,
          ...payload,
        });
        response = data;
      } catch (error) {
        throw new Error("Failed to load data from server!");
      }
      return response;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage) {
        return undefined;
      }
      return lastPage.current_page < lastPage.last_page
        ? lastPage.current_page + 1
        : undefined;
    },
    onError: (error) =>
      message.error(
        error?.message ||
          error?.data?.message ||
          "Failed to load data from server!",
      ),
    keepPreviousData: true,
    ...options,
  });
};

export const useConversationCache = () => {
  const queryClient = useQueryClient();
  const isAssignMode = useSelector(isAssignModeSelector);
  const conversation_type = useSelector(conversationTypeSelector);
  const queryKey = useMemo(() => {
    return !isAssignMode
      ? ["conversations", "list"]
      : ["conversations", "list", { inbox_filter: conversation_type }];
  }, [conversation_type, isAssignMode]);

  const appendMessageToConversationCache = useCallback(
    async ({ conversation_id: conversationId, message, conversation }) => {
      queryClient.setQueryData(queryKey, (old) => {
        let newData = cloneDeep(old);

        if (!newData || !newData.data) {
          newData = { data: [] };
        }
        const findData = newData?.data?.find(
          (row) => row.id === conversationId,
        );
        if (!findData && !isEmpty(conversation)) {
          const messages = conversation.messages || [];
          newData.data.push({
            ...conversation,
            messages: [...messages, message],
          });
        } else {
          newData.data = newData.data.map((row) => {
            if (row.id === conversationId) {
              const updateMessages = [...row.messages];
              const findMessage = updateMessages.find(
                (item) => item.id === message.id,
              );
              if (!findMessage) {
                updateMessages.push(message);
              }
              return {
                ...row,
                messages: updateMessages,
              };
            }
            return row;
          });
        }

        newData.data = sortById(newData.data);

        return newData;
      });
    },
    [queryClient, queryKey],
  );

  const appendToConversationCache = useCallback(
    async (conversation) => {
      queryClient.setQueryData(queryKey, (old) => {
        let newData = cloneDeep(old);

        if (!newData || !newData.data) {
          newData = { data: [] };
        }

        const findData = newData?.data?.find(
          (row) => row.id === conversation.id,
        );
        if (!findData) {
          newData.data.push(conversation);
        }
        newData.data = sortById(newData.data);

        return newData;
      });
    },
    [queryClient, queryKey],
  );

  // Untuk menambahkan pesa ke cache query
  const appendMessagesToConversationCache = useCallback(
    async (messages = [], queryKey = ["conversations", "list"]) => {
      queryClient.setQueryData(queryKey, (old) => {
        const newData = cloneDeep(old);
        if (!isEmpty(newData?.data) && Array.isArray(newData?.data)) {
          messages.forEach((message) => {
            newData.data = newData?.data?.map((row) => {
              if (row.id === message.conversation_id) {
                const updateMessages = [...row.messages];
                const findMessage = updateMessages.find(
                  (item) => item.id === message.id,
                );
                if (!findMessage) {
                  updateMessages.push(message);
                }
                return {
                  ...row,
                  messages: updateMessages,
                };
              }
              return row;
            });
          });
          newData.data = sortById(newData.data);
        }

        return newData;
      });
    },
    [queryClient],
  );

  const removeConversationCache = useCallback(
    async (payload = [], queryKey = ["conversations", "list"]) => {
      if (Array.isArray(payload)) {
        if (!isEmpty(payload)) {
          queryClient.setQueryData(queryKey, (old) => {
            const newData = cloneDeep(old);
            if (!isEmpty(newData?.data) && Array.isArray(newData?.data)) {
              newData.data = newData?.data?.filter((row) =>
                includes(payload, row.id),
              );
            }

            return newData;
          });
        } else {
          queryClient.setQueryData(queryKey, (old) => {
            const newData = cloneDeep(old);
            if (!isEmpty(newData?.data) && Array.isArray(newData?.data)) {
              newData.data = [];
            }

            return newData;
          });
        }
      }
    },
    [queryClient],
  );

  const updateConversationContactCache = useCallback(
    async ({ id, ...payload }) => {
      queryClient.setQueryData(queryKey, (old) => {
        const newData = cloneDeep(old);
        if (!isEmpty(newData?.data) && Array.isArray(newData?.data)) {
          newData.data = newData?.data?.map((row) => {
            if (row.contact?.id === id) {
              return { ...row, contact: { ...row.contact, ...payload } };
            }
            return row;
          });
        }

        return newData;
      });
    },
    [queryClient, queryKey],
  );

  const updateConversationSearchCache = useCallback(
    async ({ id, ...payload }) => {
      const allCaches = queryClient.getQueryCache().findAll();
      allCaches.forEach((query) => {
        if (
          Array.isArray(query.queryKey) &&
          query.queryKey.includes("conversations") &&
          query.queryKey.includes("history")
        ) {
          queryClient.setQueryData(query.queryKey, (oldData) => {
            if (!oldData) return oldData; // Jika tidak ada data, lewati
            const newPages = cloneDeep(oldData);
            newPages.pages = newPages.pages.map((page) => {
              const newPage = cloneDeep(page);
              newPage.data = newPage.data.map((item) => {
                if (item.contact?.id === id) {
                  return { ...item, contact: { ...item.contact, ...payload } };
                }
                return item;
              });
              return newPage;
            });
            return newPages;
          });
        }
      });
    },
    [queryClient],
  );

  // Untuk update status message semua conversation di cache query
  const updateStatusMessagesConversationCache = useCallback(
    async (payload = []) => {
      // JANGAN cancelQueries di sini. Event status datang sangat sering
      // (delivery/read receipt) dan bisa mendarat saat fetch daftar conversation
      // masih berjalan. cancelQueries default { revert: true } → retryer dibatalkan,
      // hasil fetch tidak pernah di-setData, dan state dikembalikan ke kondisi
      // sebelum fetch (data undefined) — padahal request-nya sendiri sukses 200 di
      // Network. Karena retry:false & refetchOnWindowFocus:false, daftar tidak
      // pulih sendiri: isinya cuma conversation hasil event (1-3 item) sampai
      // halaman di-remount. setQueryData dengan updater function tidak butuh
      // cancel — ini sisa pola optimistic-update mutation.
      queryClient.setQueryData(queryKey, (old) => {
        const newData = cloneDeep(old);
        payload.forEach((row) => {
          // Mencari indeks conversation
          let conversationIndex = undefined;

          if (!isEmpty(newData?.data) && Array.isArray(newData?.data)) {
            conversationIndex = newData.data.findIndex(
              (conv) => conv.id === row.conversation_id,
            );
          }

          // Melakukan pembaruan status
          if (
            conversationIndex !== -1 &&
            typeof conversationIndex !== "undefined"
          ) {
            // Mencari indeks pesan di dalam conversation
            const messagesFromIndex =
              newData.data[conversationIndex]?.messages || [];
            const messageIndex = messagesFromIndex.findIndex(
              (msg) => msg.id === row.message_id,
            );
            if (messageIndex !== -1) {
              newData.data[conversationIndex].messages[messageIndex].status_id =
                row.status_id;
              newData.data[conversationIndex].messages[
                messageIndex
              ].error_code_id = row.error_code_id;
            }
          }
        });

        return newData;
      });
    },
    [queryClient, queryKey],
  );

  return {
    appendMessageToConversationCache,
    appendToConversationCache,
    updateStatusMessagesConversationCache,
    removeConversationCache,
    updateConversationContactCache,
    appendMessagesToConversationCache,
    updateConversationSearchCache,
  };
};

export const conversationListQuery = (options = {}) =>
  queryOptions({
    queryKey: ["conversations", "list"],
    queryFn: async () => {
      let response;
      try {
        const {
          data: { data },
        } = await getConversations();
        response = data;
      } catch (error) {
        throw new Error("Gagal mengambil data dari server!");
      }
      return response;
    },
    placeholderData: { data: [] },
    ...options,
  });

export const conversationsLoader = (queryClient) => async () => {
  // const url = new URL(request.url);
  // const q = url.searchParams.get("q") ?? "";
  await queryClient.prefetchQuery(conversationListQuery());
  return null;
};

export const useConversationsCache = () => {
  const queryClient = useQueryClient();
  return queryClient.getQueryData(["conversations", "list"]);
};

export const useInfiniteConversationsHistory = (payload, options = {}) => {
  const { message } = useAntdMessage();
  return useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ["conversations", "history", payload],
    queryFn: async ({ pageParam = 1 }) => {
      let response;
      try {
        const {
          data: { data },
        } = await getConversationsSearch({
          page: pageParam,
          ...payload,
        });
        response = data;
      } catch (error) {
        throw new Error("Failed to load data from server!");
      }
      return response;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage) {
        return undefined;
      }
      return lastPage.current_page < lastPage.last_page
        ? lastPage.current_page + 1
        : undefined;
    },
    onError: (error) =>
      message.error(error?.message || "Failed to load data from server!"),
    keepPreviousData: true,
    ...options,
  });
};

export const conversationsHistoryLoader = (queryClient) => async () => {
  // const url = new URL(request.url);
  // console.log(url.searchParams);
  // const q = url.searchParams.get("q") ?? "";
  await queryClient.prefetchQuery(conversationListQuery());
  return null;
};

export const useUpdateContact = (options = {}) => {
  return useMutation({
    mutationKey: ["updateContact"],
    mutationFn: (payload) => updateContact(payload),
    ...options,
  });
};

export const usePostOnline = (options = {}) => {
  return useMutation({
    mutationKey: ["agent_online"],
    mutationFn: (payload) => postOnline(payload),
    ...options,
  });
};

export const useGetConversationById = ({ payload = {} }, options = {}) => {
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["getConversationId", payload],
    queryFn: async () => {
      let response;
      try {
        const {
          data: { data },
        } = await getConversation(payload.id, payload);
        response = data;
      } catch (error) {
        // if (!error.response.data.success) {
        //   navigate("/contacts", { replace: true });
        // }
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    throwOnError: (error) => {
      return message.error(
        error.message || t("error.failed_to_load_data_from_server"),
      );
    },
    ...options,
  });
};

export const useUpdateConversation = (options = {}) => {
  const queryClient = useQueryClient();
  const isAssignMode = useSelector(isAssignModeSelector);
  const conversation_type = useSelector(conversationTypeSelector);
  const queryKey = !isAssignMode
    ? ["conversations", "list"]
    : ["conversations", "list", { inbox_filter: conversation_type }];
  return useMutation({
    mutationKey: ["updateConversation"],
    mutationFn: ({ id, payload }) => updateConversation(id, payload),
    // When mutate is called:
    onMutate: async (payload) => {
      const previousData = queryClient.getQueryData(queryKey);
      if (previousData.data) {
        const updatedData = previousData.data.map((conversation) =>
          conversation.id === payload.id
            ? {
                ...conversation,
                category_id: payload.payload.category_id,
                notes: payload.payload.notes,
              }
            : conversation,
        );
        const previousDataUpdated = { ...previousData, data: updatedData };
        queryClient.setQueryData(queryKey, previousDataUpdated);
      }

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onError: (err, newData, context) => {
      queryClient.setQueryData(queryKey, context.previousData);
    },
    ...options,
  });
};

export const useTakeConversation = (options = {}) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const conversation_type = useSelector(conversationTypeSelector);
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  return useMutation({
    mutationKey: ["takeConversation"],
    mutationFn: async ({ payload }) => {
      await takeConversation(payload);
      return payload;
    },
    // onMutate: async ({ payload }) => {
    //   const previousDataUnassigned = queryClient.getQueryData([
    //     "conversations",
    //     "list",
    //     { inbox_filter: conversation_type },
    //   ]);

    //   const currentConversation = previousDataUnassigned.data.filter(
    //     (row) => row.id === payload.conversation_id
    //   );

    //   queryClient.setQueryData(
    //     [
    //       "conversations",
    //       "list",
    //       {
    //         inbox_filter: "my_inbox",
    //         // !isSupervisor && !isAdministrator ? "my_inbox" : "assigned",
    //       },
    //     ],
    //     (old) => {
    //       const oldData = old?.data || [];
    //       const newDataConversations = [...currentConversation, ...oldData];
    //       const newData = { ...old, data: newDataConversations };
    //       return newData;
    //     }
    //   );
    //   //  Return a context object with the snapshotted values
    //   return { previousDataUnassigned };
    // },
    onSuccess: (payload) => {
      if (conversation_type !== "assigned") {
        queryClient.setQueryData(
          ["conversations", "list", { inbox_filter: conversation_type }],
          (old) => {
            const newData = { ...old };
            newData.data = newData.data.filter(
              (row) => row.id !== payload.conversation_id,
            );
            return newData;
          },
        );
      }

      dispatch(setChat({ conversation: null, messages: [] }));
      dispatch(
        setConversationType(
          "my_inbox",
          // !isSupervisor && !isAdministrator ? "my_inbox" : "assigned"
        ),
      );
    },
    onError: (error) => {
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          t("assign.take_conversation_failed"),
      );
    },
    ...options,
  });
};

export const useTransferConversation = () => {
  const queryClient = useQueryClient();
  const conversation_type = useSelector(conversationTypeSelector);
  const queryKey = !conversation_type
    ? ["conversations", "list"]
    : ["conversations", "list", { inbox_filter: conversation_type }];
  return useMutation({
    mutationFn: (payload) => transferConversation(payload),
    onMutate: async (payload) => {
      if (conversation_type !== "assigned") {
        const previousData = queryClient.getQueryData(queryKey);
        // Optimistically update to the new value
        queryClient.setQueryData(queryKey, (old) => {
          const newData = { ...old };
          newData.data = newData.data.filter(
            (row) => row.id !== payload.conversation_id,
          );
          return newData;
        });
        return { previousData };
      }
    },
    onError: (err, newData, context) => {
      if (context.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
  });
};

export const useDownloadMessageMedia = () => {
  return useMutation({
    mutationFn: ({ messageId }) => downloadMessageMedia(messageId),
    onSuccess: (response, { messageId, fileName }) => {
      let filename = fileName ? fileName : `video_${messageId}.mp4`;

      // Create blob URL dan trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  });
};
